import { useTranslation } from 'react-i18next';
import { Button, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import React, { useEffect } from 'react';
import moment from 'moment';
import { useFormikContext } from 'formik';
import { AccountData, OrderData } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../../../common/getDateAndTimeFormat';
import TimeUtils from '../../../../../utils/TimeUtils';
import { holdFieldsFromNow, isPendingHoldExpired } from '../../../../../common/pendingOrderUtils';
import { SharedMobileTimePicker } from '../../../../../common/ui/SharedMobileTimePicker';
import { customerFacingPriceFormatter } from '../../../../../utils/utils';
import { EditOrderFormValues } from '../../EditOrderDialog';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';

const QUICK_HOLDS: Array<{ key: string; labelKey: string; amount: number; unit: 'hour' | 'day' | 'week' }> = [
  { key: 'oneHour', labelKey: 'paymentLinkHoldOneHour', amount: 1, unit: 'hour' },
  { key: 'oneDay', labelKey: 'paymentLinkHoldOneDay', amount: 1, unit: 'day' },
  { key: 'twoDays', labelKey: 'paymentLinkHoldTwoDays', amount: 2, unit: 'day' },
  { key: 'oneWeek', labelKey: 'paymentLinkHoldOneWeek', amount: 1, unit: 'week' },
];

const selectedQuickHoldSx = {
  borderWidth: '2px',
  fontWeight: 700,
  '&:hover': { borderWidth: '2px' },
};

type PaymentLinkHoldFieldsProps = {
  // Set when the dialog edits an existing pending order: the fields then edit the hold on that order.
  pendingOrder?: OrderData;
};

export const PaymentLinkHoldFields = ({ pendingOrder }: PaymentLinkHoldFieldsProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const setHoldFields = (holdDate: string, holdTime: string, quickKey: string) => {
    void setFieldValue('paymentLinkHoldDate', holdDate);
    void setFieldValue('paymentLinkHoldTime', holdTime);
    void setFieldValue('paymentLinkHoldQuickKey', quickKey);
  };

  return (
    <Grid container direction="column" gap={1} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{t('calendar.edit_order.paymentLinkHold')}</Typography>
      {!pendingOrder && (
        <Grid container gap={1}>
          <Button
            size="small"
            variant="outlined"
            sx={values.paymentLinkHoldQuickKey === 'noLimit' ? selectedQuickHoldSx : undefined}
            onClick={() => setHoldFields('', '', 'noLimit')}>
            {t('calendar.edit_order.paymentLinkHoldNoLimitButton')}
          </Button>
          {QUICK_HOLDS.map((quickHold) => (
            <Button
              key={quickHold.key}
              size="small"
              variant="outlined"
              sx={values.paymentLinkHoldQuickKey === quickHold.key ? selectedQuickHoldSx : undefined}
              onClick={() => {
                const { holdDate, holdTime } = holdFieldsFromNow(quickHold.amount, quickHold.unit, account.timezone);
                setHoldFields(holdDate, holdTime, quickHold.key);
              }}>
              {t(`calendar.edit_order.${quickHold.labelKey}`)}
            </Button>
          ))}
        </Grid>
      )}
      <Grid container gap={1} alignItems="center">
        <DatePicker
          value={values.paymentLinkHoldDate ? moment(values.paymentLinkHoldDate) : null}
          minDate={moment()}
          format={getDateFormat(account).longDateFormat}
          onChange={(newDate) => {
            if (newDate) {
              void setFieldValue('paymentLinkHoldDate', newDate.format('YYYY-MM-DD'));
              void setFieldValue('paymentLinkHoldQuickKey', '');
            } else {
              setHoldFields('', '', 'noLimit');
            }
          }}
          slotProps={{
            textField: { size: 'small', sx: { width: '170px' } },
            field: { clearable: true },
          }}
        />
        <SharedMobileTimePicker
          value={moment(values.paymentLinkHoldTime || '23:59', 'HH:mm')}
          disabled={!values.paymentLinkHoldDate}
          minutesStep={15}
          size="small"
          onChange={(newTime) => {
            if (newTime) {
              void setFieldValue('paymentLinkHoldTime', newTime.format('HH:mm'));
              void setFieldValue('paymentLinkHoldQuickKey', '');
            }
          }}
        />
      </Grid>
      <Typography variant="caption" color="text.secondary">
        {!values.paymentLinkHoldDate
          ? t('calendar.edit_order.paymentLinkHoldNoLimit')
          : pendingOrder
          ? t('calendar.edit_order.paymentLinkHoldEditHint')
          : t('calendar.edit_order.paymentLinkHoldHint')}
      </Typography>
    </Grid>
  );
};

type PaymentLinksFieldsProps = {
  // Set when the dialog edits an existing pending order: the fields then edit the hold on that
  // order instead of offering to create a payment link.
  pendingOrder?: OrderData;
  // Set when a gift card covers only part of the price: the rest can only be collected with a link.
  voucherRemainderAmount?: number;
};

export const PaymentLinksFields = ({ pendingOrder, voucherRemainderAmount }: PaymentLinksFieldsProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  // @ts-ignore
  const { setCreatePaymentLink } = useEditOrder();

  const showHoldControls = !!pendingOrder || values.createPaymentLink;

  useEffect(() => {
    if (voucherRemainderAmount && !values.createPaymentLink) {
      void setFieldValue('createPaymentLink', true);
    }
  }, [voucherRemainderAmount, values.createPaymentLink]);

  const currentHoldLabel = () => {
    if (!pendingOrder) {
      return undefined;
    }
    if (isPendingHoldExpired(pendingOrder)) {
      return t('calendar.order_popper.pendingHoldExpired');
    }
    if (!pendingOrder.pending_expires_at) {
      return t('calendar.edit_order.paymentLinkHoldNoLimit');
    }
    const expiry = TimeUtils.getLocalTime(pendingOrder.pending_expires_at, account.timezone);
    const { shortDateFormat } = getDateFormat(account);
    return `${t('calendar.order_popper.pendingHoldUntil')} ${expiry.format(
      `${shortDateFormat} ${getTimeFormat(account)}`,
    )}`;
  };

  return (
    <Grid container direction="column">
      {pendingOrder ? (
        <Typography variant="body2" color="text.secondary">
          {currentHoldLabel()}
        </Typography>
      ) : (
        <FormControlLabel
          control={<Checkbox />}
          checked={values.createPaymentLink}
          disabled={!!voucherRemainderAmount}
          label={t('calendar.edit_order.createPaymentLink')}
          onChange={(e: any) => {
            void setFieldValue('createPaymentLink', e.target.checked);
            setCreatePaymentLink(e.target.checked);
          }}
        />
      )}
      {!!voucherRemainderAmount && (
        <Typography variant="caption" color="text.secondary">
          {t('vouchers.redeemRemainderLink', {
            amount: customerFacingPriceFormatter(voucherRemainderAmount, account),
          })}
        </Typography>
      )}
      {showHoldControls && <PaymentLinkHoldFields pendingOrder={pendingOrder} />}
    </Grid>
  );
};
